import React from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import { ButtonComponent, type ButtonComponentProps } from './ButtonComponent';

export interface LookupButtonProps extends Omit<ButtonComponentProps, 'startIcon' | 'children'> {
  /** Button text - defaults to "Buscar" */
  children?: React.ReactNode;
  /** Text to show when loading - defaults to "Cargando..." */
  loadingText?: string;
  /** Custom search icon - defaults to SearchIcon */
  searchIcon?: React.ReactNode;
  /** Whether to show search icon - defaults to true */
  showSearchIcon?: boolean;
}

/**
 * LookupButton - Reusable Lookup Button Component
 *
 * A specialized button component for lookup/search operations that provides:
 * - Built-in search icon (customizable)
 * - Consistent styling for lookup operations
 * - Loading state with appropriate feedback
 * - Based on the standardized ButtonComponent
 *
 * @example
 * ```tsx
 * <LookupButton
 *   onClick={handleOpenModal}
 *   loading={isLoading}
 *   disabled={disabled}
 * >
 *   Buscar Artículo
 * </LookupButton>
 * ```
 */
export const LookupButton: React.FC<LookupButtonProps> = ({
  children = 'Buscar',
  loadingText = 'Cargando...',
  searchIcon = <SearchIcon />,
  showSearchIcon = true,
  variant = 'outlined',
  ...buttonProps
}) => {
  return (
    <ButtonComponent
      variant={variant}
      startIcon={showSearchIcon ? searchIcon : undefined}
      loadingText={loadingText}
      sx={{
        textTransform: 'none',
        fontWeight: 500,
        ...buttonProps.sx,
      }}
      {...buttonProps}
    >
      {children}
    </ButtonComponent>
  );
};

export default LookupButton;